'use client';

import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { cn } from 'tailwind-variants';
import { flipCardVariants } from './FlipCards.styles';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { IStandardContentPageComponents } from '@/.generated';

type CardItem = NonNullable<IStandardContentPageComponents['flip_cards']['cards']>[number];
type CardSide = CardItem['card_sides'][number];

interface FlipCardProps {
  front: CardSide;
  back?: CardSide;
  index: number;
  isActive: boolean;
  onToggle: (index: number) => void;
}

interface FaceProps {
  side: CardSide;
  isVisible: boolean;
  isFlippable: boolean;
  isBack?: boolean;
  flipLabel?: string;
  onFlip?: () => void;
}

// Renders a single card face with optional background image/color and rich text content
const Face = ({ side, isVisible, isFlippable, isBack = false, flipLabel, onFlip }: FaceProps) => {
  const faceRef = useRef<HTMLDivElement>(null);

  // Keep the off-screen face out of the tab order: links/buttons in the hidden face must not be
  // reachable by Tab. Only the currently visible face's interactive content stays focusable, so
  // after a flip the next Tab lands on the newly shown face's link rather than a hidden one.
  useEffect(() => {
    const node = faceRef.current;
    if (!node) return;

    const applyTabIndex = () => {
      const focusables = node.querySelectorAll<HTMLElement>(
        'a, button, input, textarea, select, [tabindex]'
      );
      focusables.forEach((el) => {
        if (isVisible) {
          const prev = el.getAttribute('data-flip-tabindex');
          if (prev !== null) {
            if (prev === '') el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', prev);
            el.removeAttribute('data-flip-tabindex');
          }
        } else {
          if (el.getAttribute('data-flip-tabindex') === null) {
            el.setAttribute('data-flip-tabindex', el.getAttribute('tabindex') ?? '');
          }
          el.setAttribute('tabindex', '-1');
        }
      });
    };

    applyTabIndex();

    // RichTextWrapper renders its links via dangerouslySetInnerHTML, which can populate or replace
    // these nodes after this effect first runs (content/context resolution, live preview). Re-apply
    // on any subtree change so the hidden face never leaks focusable links back into the tab order.
    const observer = new MutationObserver(applyTabIndex);
    observer.observe(node, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isVisible]);

  const bgImageUrl = side?.card_background_image?.url;
  const bgImageAlt = side?.image_alt_text ?? '';
  const bgColor = side?.card_background_color?.color_list ?? undefined;
  const textColor = side?.card_text_color?.color_list ?? undefined;
  const hasTextOverride = Boolean(textColor);
  const hasImage = !!bgImageUrl;

  const contentClass = hasTextOverride ? '**:text-inherit!' : '**:text-white!';

  const { face, bgImage, bgOverlay, content, flipIcon } = flipCardVariants({
    isFlippable,
    isVisible,
    isBack,
  });

  return (
    <div
      ref={faceRef}
      className={face()}
      style={{
        ...(!hasImage && bgColor ? { backgroundColor: bgColor } : {}),
        color: textColor,
      }}
      // inert (not aria-hidden) keeps the hidden face and its links out of both the
      // tab order and the accessibility tree, even before hydration
      inert={!isVisible}
    >
      {hasImage && (
        <>
          <ImageWrapper
            image={toEnhancedImage(side?.card_background_image, {
              cslp: side?.$?.card_background_image,
            })}
            alt={bgImageAlt}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            // Cover the positioned face so the overlay and content stack above it
            wrapperClassName="absolute inset-0"
            imageClassName={bgImage()}
            loading="lazy"
          />
          <div className={bgOverlay()} aria-hidden="true" />
        </>
      )}
      <RichTextWrapper
        content={side?.card_content}
        cslpAttribute={side?.$?.card_content}
        className={content({ className: contentClass })}
        style={textColor ? { color: textColor } : undefined}
      />
      {isFlippable && (
        <button type="button" className={flipIcon()} aria-label={flipLabel} onClick={onFlip}>
          <i className="fa-kit fa-flip-card-icon" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

// True if the event target is a real interactive child of the card (not the card itself)
const isNestedInteractive = (
  target: EventTarget | null,
  currentTarget: EventTarget | null
): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target === currentTarget) return false;
  return !!target.closest('a, button, input, textarea, select');
};

const isTouchDevice = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

// Single flip-card tile
export const FlipCard = ({ front, back, index, isActive, onToggle }: FlipCardProps) => {
  const isFlippable = !!back;

  // Desktop flips via CSS :hover, so React state alone doesn't know the back face is showing.
  // Track hover (pointer devices only) so the visible-face attributes (inert/tabindex) stay in
  // sync with what's actually on screen — otherwise the hover-revealed back face stays `inert`
  // and can't be scrolled even when its content overflows.
  const [isHovered, setIsHovered] = useState(false);

  const isFlipped = isFlippable && (isActive || isHovered);
  const { scene, card, interactive } = flipCardVariants({ isFlippable, isFlipped });

  // Tap toggles flip on ≤768px (desktop uses CSS :hover). Tapping a flipped card flips it back;
  // clicks on nested links/buttons fire natively (e.g. redirect) instead of unflipping.
  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!isFlippable) return;
      if (!isTouchDevice()) return;
      if (isNestedInteractive(e.target, e.currentTarget)) return;
      onToggle(index);
    },
    [isFlippable, onToggle, index]
  );

  // Hover only drives the flip on pointer devices; touch uses the tap handler above so we ignore
  // the emulated mouseenter/leave that fire after a tap.
  const handleMouseEnter = useCallback(() => {
    if (!isFlippable || isTouchDevice()) return;
    setIsHovered(true);
  }, [isFlippable]);

  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice()) return;
    setIsHovered(false);
  }, []);

  // The whole card must not be role=button (its rich text contains links, and a
  // button may not contain links). Touch flip stays on the div; keyboard flip and the
  // accessible name live on the real flip-icon button rendered inside each face.
  const handleFlip = useCallback(() => onToggle(index), [onToggle, index]);

  return (
    <div
      className={scene()}
      data-flip-card-scene
      {...(isFlippable ? { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave } : {})}
    >
      <div
        className={cn(card(), isFlippable && interactive())}
        {...(isFlippable ? { onClick: handleClick } : {})}
      >
        <Face
          side={front}
          isVisible={!isFlipped}
          isFlippable={isFlippable}
          flipLabel={`Show back of card ${index + 1}`}
          onFlip={handleFlip}
        />
        {isFlippable && (
          <Face
            side={back!}
            isVisible={isFlipped}
            isFlippable={isFlippable}
            isBack
            flipLabel={`Show front of card ${index + 1}`}
            onFlip={handleFlip}
          />
        )}
      </div>
    </div>
  );
};
